import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { CheckCircle2, Loader2, Calendar, MapPin, LinkIcon } from "lucide-react";
import { IconFileText, IconUpload } from "@tabler/icons-react";
import {
  useLookupBookingByLinkMutation,
  useCancelBookingByLinkMutation,
} from "@/app/booking-flow/publicBookingFlowApi";
import type { BookingDetails } from "@/app/booking-flow/publicBookingFlowTypes";
import { BOOKING_TIME_ZONE } from "../booking-flow/bookingTimeZone";
import { documentsAvailability, useGetPublicBookingDocumentsQuery } from "@/app/document-upload/publicDocumentsApi";
import { LOCKED_OUT_MESSAGE, NOT_FOUND_MESSAGE } from "./manageLinkCopy";

const EXPIRED_LINK_MESSAGE =
  "This cancellation link is no longer valid. You can still manage your appointment by entering your phone number and confirmation number.";
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

// 410 is the link itself having lapsed, which is a different thing to tell someone than a wrong
// code: there is nothing they can retype here that will help, so the message has to send them to
// the manage page instead. See AmsBookingsController.ExpiredLink.
function errorMessageFor(error: unknown): string {
  const status = (error as { status?: number })?.status;
  if (status === 404) return NOT_FOUND_MESSAGE;
  if (status === 410) return EXPIRED_LINK_MESSAGE;
  if (status === 429) return LOCKED_OUT_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

function isExpiredLink(error: unknown): boolean {
  return (error as { status?: number })?.status === 410;
}

/**
 * The way to the documents list (AB#598). The confirmation text and email carry only this page's link,
 * so until online upload ships (AB#603) this is how a household gets from a message to what to bring.
 * It is rendered outside the page's states, so it shows whatever state the page is in: before the code
 * is entered, after lookup, after cancelling, and with a missing, expired or rejected link.
 */
export function WhatToBringLink() {
  return (
    <div className="w-full max-w-lg mx-auto">
      <Button asChild variant="outline" className="w-full h-12 text-base">
        <Link to="/booking/document-upload" data-testid="what-to-bring-link">
          <IconFileText className="w-5 h-5" aria-hidden="true" />
          What to bring to your appointment
        </Link>
      </Button>
    </div>
  );
}

/**
 * The way to the customer documents page (AB#774) for this appointment, with the same link token: the
 * confirmation number is asked for again there, since that page earns its own grant.
 */
export function UploadDocumentsLink({ token }: { token: string }) {
  return (
    <Button asChild variant="outline" className="w-full h-12 text-base">
      <Link to={`/booking/document-upload?t=${encodeURIComponent(token)}`} data-testid="upload-documents-link">
        <IconUpload className="w-5 h-5" aria-hidden="true" />
        Upload documents
      </Link>
    </Button>
  );
}

function formatAppointmentDateTime(startIso: string): string {
  return new Date(startIso).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: BOOKING_TIME_ZONE
  });
}

/**
 * Landing page for the cancellation link in a booking-details SMS (`/booking/cancel?t=…`).
 *
 * Holding the link is not enough to cancel anything: it resolves server-side to the phone number it
 * was minted for, and the 6-digit confirmation code from the message body is still required. That
 * keeps a forwarded or intercepted link harmless, and it is also why this page never cancels on
 * load — a GET that cancelled would fire the moment any messaging app, link scanner, or mail
 * preview fetched the URL, with no one having tapped anything.
 */
export default function BookingCancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") ?? "";

  const [lookupBookingByLink, { isLoading: lookupLoading }] = useLookupBookingByLinkMutation();
  const [cancelBookingByLink, { isLoading: cancelLoading }] = useCancelBookingByLinkMutation();

  const [codeInput, setCodeInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);

  // Whether customer documents are on (AB#774), from the documents list's answer; there is no public
  // flag endpoint. Only decides whether the found appointment offers the Upload documents link.
  const documentsOn = documentsAvailability(useGetPublicBookingDocumentsQuery(undefined, { refetchOnMountOrArgChange: true })) === "on";

  const code = codeInput.trim();
  const canSubmit = token !== "" && code !== "";

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLookupError("");

    try {
      setBooking(await lookupBookingByLink({ token, confirmationCode: code }).unwrap());
    } catch (error) {
      setLinkExpired(isExpiredLink(error));
      setLookupError(errorMessageFor(error));
    }
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    setCancelError("");

    try {
      const result = await cancelBookingByLink({
        token,
        confirmationCode: code,
        cancellationReason: reasonInput.trim() || undefined,
      }).unwrap();

      setCancelledAt(result.cancelledAt);
    } catch (error) {
      setCancelError(errorMessageFor(error));
    }
  };

  const manageLink = (
    <Button asChild variant="ghost" className="w-full text-slate-400">
      <Link to="/booking/manage">Manage Your Appointment</Link>
    </Button>
  );

  // A link with no token at all never came from one of our messages — most likely someone opened a
  // truncated URL. There is nothing to look up, so the page goes straight to the manage page rather
  // than asking for a code it could not use.
  const missingToken = token === "";

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4 sm:px-6 lg:px-8 py-8">
        {cancelledAt ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-emerald-100 flex items-center justify-center rounded-full">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Appointment Cancelled</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                If you need energy assistance, please schedule a new appointment when the next set of appointments becomes available.
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6">
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : missingToken || linkExpired ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-slate-100 flex items-center justify-center rounded-full">
                <LinkIcon className="w-8 h-8 text-slate-400" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Link No Longer Valid</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                {EXPIRED_LINK_MESSAGE}
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              {manageLink}
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : booking ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Your Appointment</CardTitle>
              <CardDescription className="text-slate-500">
                Cancelling this appointment can't be undone. You'd need to book a new one.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="bg-slate-50 p-5 rounded-lg border space-y-4">
                <div className="flex gap-3">
                  <Calendar className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Appointment</span>
                    <span className="font-medium text-sm mt-0.5">{formatAppointmentDateTime(booking.start)}</span>
                  </div>
                </div>

                {(booking.siteName || booking.location) && (
                  <div className="flex gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Location</span>
                      <span className="font-medium text-sm mt-0.5">
                        {booking.siteName}
                        {booking.siteName && booking.location ? " — " : ""}
                        {booking.location}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <form className="space-y-2" id="booking-cancel-form" onSubmit={handleCancel}>
                <Label htmlFor="reason">Reason for Cancelling</Label>
                <Textarea
                  id="reason"
                  name="reason"
                  placeholder="Optional"
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                />
                {cancelError !== "" && <FieldError>{cancelError}</FieldError>}
              </form>

              {documentsOn && <UploadDocumentsLink token={token} />}
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                form="booking-cancel-form"
                type="submit"
                variant="destructive"
                className="w-full text-base h-12"
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cancelling…
                  </span>
                ) : (
                  "Cancel Appointment"
                )}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={() => setBooking(null)}>
                Keep Appointment
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Cancel Your Appointment</CardTitle>
              <CardDescription className="text-slate-500">
                For your security, enter the 6-digit confirmation number from your appointment text message.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form className="space-y-4" id="booking-code-form" onSubmit={handleLookup}>
                <div className="space-y-2">
                  <Label htmlFor="code">Confirmation Number <span aria-hidden="true">*</span></Label>
                  <Input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit confirmation number"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    required
                  />
                </div>

                {lookupError !== "" && <FieldError>{lookupError}</FieldError>}
              </form>
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                form="booking-code-form"
                type="submit"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                disabled={lookupLoading || !canSubmit}
              >
                {lookupLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking…
                  </span>
                ) : (
                  "Continue"
                )}
              </Button>
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        )}

        <WhatToBringLink />
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}
